// Live coaching commands from the web (COACH-01..04): one idempotent POST per click, refusal codes mapped to sentences,
// and the call summary the monitor view shows. No retries: a click that may have reached the server says so.
import type { Call } from '@bcio-voice/contracts';
import { ApiRequestError, type CoachingClient } from '../../lib/api.ts';
import { COACH_REFUSAL_TEXT, type CoachMode } from './coaching.ts';

export type CoachActionName = 'coach_listen' | 'coach_whisper' | 'coach_stop';
export type CoachOutcome = { ok: true; commandId: string | null } | { ok: false; code: string; text: string; requestId: string | null };

export const UNCONFIRMED_TEXT = 'The phone system did not confirm the request. Check the call before you try again.';
export const GENERIC_FAILURE_TEXT = 'The coaching request could not be completed.';

/** A fresh Idempotency-Key per click (the API requires 8 to 128 of [a-zA-Z0-9_.:-]). */
export function newCoachKey(random: () => string = () => globalThis.crypto.randomUUID()): string {
  return `coach-${random()}`;
}

export async function runCoachCommand(client: Pick<CoachingClient, 'command'>, callId: string, action: CoachActionName, deviceId: string | null, key: string = newCoachKey()): Promise<CoachOutcome> {
  try {
    const body = action !== 'coach_stop' && deviceId ? { action, device_id: deviceId } : { action };
    const receipt = await client.command(callId, body, key);
    return { ok: true, commandId: receipt?.data?.command_id ?? null };
  } catch (error) {
    if (error instanceof ApiRequestError) {
      const text = COACH_REFUSAL_TEXT[error.code] ?? (error.outcomeUnknown ? UNCONFIRMED_TEXT : GENERIC_FAILURE_TEXT);
      return { ok: false, code: error.code, text, requestId: error.requestId };
    }
    return { ok: false, code: 'unknown', text: GENERIC_FAILURE_TEXT, requestId: null };
  }
}

/** What the monitor says after an accepted command (the controller applies it asynchronously). */
export function acceptedText(action: CoachActionName, joined: boolean): string {
  if (action === 'coach_stop') return 'Stopping coaching.';
  if (joined) return action === 'coach_whisper' ? 'Switching to whisper: the agent will hear you; the other party will not.' : 'Switching to listen: nobody on the call will hear you.';
  return 'Your phone will ring. Answer it to join the call audio.';
}

const DIRECTION: Record<Call['direction'], string> = { inbound: 'Inbound', outbound: 'Outbound', internal: 'Internal' };
const STATE: Record<Call['state'], string> = { created: 'Connecting', ringing: 'Ringing', active: 'Talking', held: 'On hold', ended: 'Ended', failed: 'Failed' };
export type SummaryRow = { label: string; value: string };

/** The call summary: direction, state, agents (by name when known), the other party's number, and who coaches. */
export function callSummary(call: Call, selfId: string, name: (id: string) => string | null, formatNumber: (e164: string) => string): SummaryRow[] {
  const agents = call.participants.filter(p => p.kind === 'agent' && p.user_id).map(p => name(p.user_id!) ?? 'Team member');
  const party = call.direction === 'inbound' ? call.from.e164 : call.destination.kind === 'pstn' ? call.destination.e164 : null;
  const c = call.coaching;
  const coach = !c ? 'Nobody' : c.coach_user_id === selfId ? (c.state === 'dialing' ? 'You (your phone is ringing)' : `You (${modeWord(c.mode)})`) : `Another coach (${modeWord(c.mode)})`;
  return [
    { label: 'Direction', value: DIRECTION[call.direction] ?? call.direction },
    { label: 'State', value: STATE[call.state] ?? call.state },
    { label: call.direction === 'internal' ? 'People' : 'Agent', value: agents.length ? agents.join(', ') : 'No one yet' },
    { label: 'Other party', value: party ? formatNumber(party) : call.direction === 'internal' ? 'Internal call' : 'Unknown number' },
    { label: 'Coach', value: coach },
  ];
}

export function modeWord(mode: CoachMode): string {
  return mode === 'whisper' ? 'whispering' : 'listening';
}

/** True on the coach's own device leg: the API lists the coach as a supervisor only in their own view of the call. */
export function isCoachingLeg(call: Pick<Call, 'coaching' | 'participants'>, userId: string | null): boolean {
  return !!userId && call.coaching?.coach_user_id === userId && call.participants.some(p => p.user_id === userId && p.kind === 'supervisor');
}
